import { Card, CardContent } from "@designs-by-maryam/ui/components/card";
import { Clock, Mail, MapPin, Phone } from "lucide-react";
import { useSiteContact } from "@/hooks/use-site-contact";

type ContactItemProps = {
	icon: React.ReactNode;
	label: string;
	value: string;
	href?: string;
	subValue?: string;
};

function ContactItem({ icon, label, value, href, subValue }: ContactItemProps) {
	const content = href ? (
		<a
			className="group/link font-medium text-foreground transition-all duration-200 hover:text-primary"
			href={href}
		>
			<span className="border-transparent border-b transition-colors group-hover/link:border-primary">
				{value}
			</span>
		</a>
	) : (
		<span className="font-medium text-foreground">{value}</span>
	);

	return (
		<div className="group flex items-start gap-4 rounded-lg p-3 transition-all hover:bg-primary/5 dark:hover:bg-primary/10">
			<div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary shadow-sm transition-all duration-200 group-hover:scale-110 group-hover:shadow-md dark:bg-primary/20 dark:text-primary">
				{icon}
			</div>
			<div className="space-y-1">
				<p className="text-muted-foreground text-sm">{label}</p>
				{content}
				{subValue && (
					<p className="text-muted-foreground text-sm">{subValue}</p>
				)}
			</div>
		</div>
	);
}

export function ContactInfo() {
	const { data: contact } = useSiteContact();

	return (
		<Card className="h-fit overflow-hidden shadow-lg transition-shadow duration-300 hover:shadow-xl">
			<div className="-translate-y-10 absolute top-0 right-0 h-32 w-32 translate-x-10 rounded-full bg-primary/10 blur-xl dark:bg-primary/5" />
			<CardContent className="relative space-y-6 pt-6">
				<div className="space-y-3 text-center">
					<div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary shadow-sm dark:bg-primary/20 dark:text-primary">
						<Mail className="h-7 w-7" />
					</div>
					<h2 className="font-semibold text-xl tracking-tight">Get in Touch</h2>
					<p className="text-muted-foreground text-sm">
						We'd love to hear from you. Reach out through any of the channels
						below.
					</p>
				</div>

				<div className="space-y-2">
					<ContactItem
						href={contact.phoneHref}
						icon={<Phone className="h-5 w-5" />}
						label="Phone"
						value={contact.phone}
					/>

					<ContactItem
						href={contact.emailHref}
						icon={<Mail className="h-5 w-5" />}
						label="Email"
						value={contact.email}
					/>

					<ContactItem
						icon={<MapPin className="h-5 w-5" />}
						label="Location"
						value={contact.location}
					/>

					<ContactItem
						icon={<Clock className="h-5 w-5" />}
						label="Business Hours"
						subValue={contact.businessHours.weekend}
						value={contact.businessHours.weekdays}
					/>
				</div>

				<div className="relative overflow-hidden rounded-lg border border-primary/20 bg-gradient-to-br from-primary/5 to-primary/10 p-4 dark:border-primary/30 dark:from-primary/10 dark:to-primary/5">
					<div className="absolute inset-0 bg-[length:20px_20px] bg-[linear-gradient(45deg,transparent_25%,rgba(20,184,166,0.05)_50%,transparent_75%)]" />
					<div className="relative flex items-start gap-2">
						<svg
							aria-hidden="true"
							className="mt-0.5 h-4 w-4 shrink-0 text-primary"
							fill="none"
							stroke="currentColor"
							strokeWidth="2"
							viewBox="0 0 24 24"
						>
							<path
								d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
						</svg>
						<p className="font-medium text-foreground text-sm">
							{contact.responseTime}
						</p>
					</div>
				</div>
			</CardContent>
		</Card>
	);
}
